import React from 'react';
// External imports
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
  withStyles,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
// Internal imports
import { PRIOR_YEAR_NOTICE_MODE } from '~/app/constants.js';
// Styling imports
import { styles } from '~/app/Components/PriorYearNotice/css/priorYearNotice.js';

/**
 * Prior Year Fee notice (CORP-1199).
 *
 * Renders in one of two modes, and the difference is not cosmetic.
 *
 * `acknowledgment` is mandatory. It is the affirmative record legal asked for,
 * it blocks entry into the return, and it is shown once per prior tax year
 * regardless of the preparer's reminder preference.
 *
 * `reminder` is informational. It appears in a season already acknowledged, is
 * dismissible, and is what the checkbox actually switches off.
 *
 * The two are titled and buttoned differently on purpose. A preparer who
 * acknowledges TY2024, checks the box, then opens a TY2023 return will be asked
 * to acknowledge again, because that is a separate tax year with no record. If
 * both modes looked identical the checkbox would read as broken. The
 * acknowledgment title plus "Required once for each prior tax year" is what makes
 * that behavior legible rather than surprising.
 *
 * @component
 * @category PriorYearNotice
 */
const PriorYearNoticeModal = props => {
  const { classes, notice, onAcknowledge, onDismiss } = props;

  const isAcknowledgment = notice?.mode === PRIOR_YEAR_NOTICE_MODE.ACKNOWLEDGMENT;

  // The checkbox is phrased negatively ("Do not remind me again") while the
  // stored setting is positive (showPriorYearFeeNotice), so checked === setting
  // off. Seeding from remindersEnabled matters: a preparer who already switched
  // reminders off can still receive a mandatory acknowledgment, and the box must
  // come up already checked so leaving it alone preserves their preference
  // instead of silently switching reminders back on.
  const [suppressReminders, setSuppressReminders] = React.useState(
    notice?.remindersEnabled === false,
  );

  const [submitting, setSubmitting] = React.useState(false);

  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!notice?.mode) return null;

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      if (isAcknowledgment) {
        await onAcknowledge(notice.noticeVersionID, suppressReminders);
      } else {
        onDismiss(suppressReminders);
      }
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={true}
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="sm"
      aria-labelledby="prior-year-notice-title"
      aria-describedby="prior-year-notice-body"
    >
      <DialogTitle id="prior-year-notice-title" disableTypography>
        <Typography variant="h6">
          {isAcknowledgment ? 'Prior Year Fee Acknowledgment' : 'Prior Year Fee Reminder'}
        </Typography>
        {/* The tax year is the single most useful thing on this modal. Without it
            a preparer who already acknowledged one prior year cannot tell a new
            year's acknowledgment apart from a repeat of the one they dismissed,
            since the copy is usually the global version and therefore identical.
            Seasons render as tax year, matching the rest of CLO. */}
        {!!notice.season && (
          <Typography variant="subtitle2" className={classes.taxYear}>
            {`Tax Year ${notice.season - 1}`}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent id="prior-year-notice-body">
        {/* Legal's own heading, stored on the version row. Distinct from the
            modal title above, which only distinguishes the two modes. */}
        <Typography variant="subtitle1" className={classes.noticeTitle}>
          {notice.noticeTitle}
        </Typography>

        <Typography variant="body2" className={classes.noticeBody}>
          {notice.noticeBody}
        </Typography>

        {/* When reminders are already switched off, say so. Otherwise this modal
            looks like the setting failed: they turned the notice off, and here it
            is again with wording they have read before. Naming the setting and the
            per-year rule together is what makes the reappearance make sense. */}
        {isAcknowledgment && (
          <Typography variant="caption" className={classes.requiredNote}>
            {notice.remindersEnabled === false
              ? 'You have reminders switched off, but each prior tax year has to be acknowledged once. This is the first time you have worked in this one.'
              : 'Required once for each prior tax year.'}
          </Typography>
        )}

        {notice.canSetReminderPreference !== false && (
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                id="chkDoNotRemindPriorYearFee"
                checked={suppressReminders}
                disabled={submitting}
                onChange={() => setSuppressReminders(!suppressReminders)}
                icon={<CheckBoxOutlineBlankIcon className={classes.checkBoxOutlineBlankIcon} />}
                checkedIcon={<CheckBoxIcon className={classes.checkBoxIcon} color="primary" />}
              />
            }
            label="Do not remind me again"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button
          id="btnConfirmPriorYearNotice"
          variant="contained"
          color="primary"
          size="medium"
          disabled={submitting}
          onClick={handleConfirm}
        >
          {isAcknowledgment ? 'Acknowledge' : 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(PriorYearNoticeModal);
